import { type DBSchema, type IDBPDatabase, openDB } from "idb";

interface BibleCacheDB extends DBSchema {
	verses: {
		key: string;
		value: {
			key: string;
			html: string;
			updatedAt: number;
		};
	};
}

const DB_NAME = "hallv-bible-cache";
const DB_VERSION = 1;
const STORE_VERSES = "verses";

let dbPromise: Promise<IDBPDatabase<BibleCacheDB>> | null = null;

function canUseIndexedDB(): boolean {
	// `indexedDB` is a global in browsers that support it.
	return typeof indexedDB !== "undefined";
}

async function getDb(): Promise<IDBPDatabase<BibleCacheDB> | null> {
	if (!canUseIndexedDB()) return null;

	if (!dbPromise) {
		dbPromise = openDB<BibleCacheDB>(DB_NAME, DB_VERSION, {
			upgrade(db) {
				if (!db.objectStoreNames.contains(STORE_VERSES)) {
					db.createObjectStore(STORE_VERSES, { keyPath: "key" });
				}
			},
		});
	}

	try {
		return await dbPromise;
	} catch {
		// If IndexedDB fails (e.g., private mode restrictions), disable caching.
		return null;
	}
}

export async function getCachedVerse(key: string): Promise<string | null> {
	const db = await getDb();
	if (!db) return null;

	const row = await db.get(STORE_VERSES, key);
	return row?.html ?? null;
}

export async function setCachedVerse(key: string, html: string): Promise<void> {
	const db = await getDb();
	if (!db) return;

	await db.put(STORE_VERSES, { key, html, updatedAt: Date.now() });
}

export async function setCachedVerses(
	entries: Array<{ key: string; html: string }>,
): Promise<void> {
	if (entries.length === 0) return;

	const db = await getDb();
	if (!db) return;

	const tx = db.transaction(STORE_VERSES, "readwrite");
	for (const { key, html } of entries) {
		await tx.store.put({ key, html, updatedAt: Date.now() });
	}
	await tx.done;
}

/**
 * Cleanup: remove legacy verse-cache entries from localStorage.
 * Verse cache is IndexedDB-only now, but users may still have old keys lingering.
 */
const LEGACY_VERSE_CACHE_KEY_RE = /^(en|[A-Z]{2,3})\|/;

export function purgeLegacyVerseCacheFromLocalStorage(): void {
	try {
		// Iterate backwards because we're mutating storage.
		for (let i = localStorage.length - 1; i >= 0; i--) {
			const key = localStorage.key(i);
			if (!key) continue;
			if (LEGACY_VERSE_CACHE_KEY_RE.test(key)) {
				localStorage.removeItem(key);
			}
		}
	} catch {
		// ignore
	}
}

// Run once on module load so old verse keys disappear immediately.
purgeLegacyVerseCacheFromLocalStorage();
